import React from "react";
import { SEOHelmet } from "../components/common/SEOHelmet";
import { PATH_ADDITIONAL_OFFERS } from "../utils/constants";
import { TestimonialsComponent } from "../components/common/testimonial/TestimonialComponent";
import { BookOpenIcon, AcademicCapIcon, HandRaisedIcon, HeartIcon, UserGroupIcon, BoltIcon } from "@heroicons/react/24/outline";

type OfferSection = {
  title: string;
  content: string[];
  details?: {
    label: string;
    value: string;
  }[];
  texts?: string[];
  isMainSection?: boolean;
  icon?: "book" | "academic" | "hand" | "heart" | "group" | "bolt";
};

const data: { sections: OfferSection[] } = {
  sections: [
    {
      title: "Weitere Angebote",
      content: [],
      texts: [
        "Neben der klassischen Ergotherapie bieten wir auch weitere Schulungen und Kurse an. Hier finden Sie eine Übersicht über die aktuellen Angebote.",
      ],
      isMainSection: true,
    },
    {
      title: "Legasthenietraining nach der AFS-Methode",
      content: [],
      texts: [
        "Das Training unterstützt Ihr Kind dabei, seine Lese- und Schreibfähigkeit zu verbessern. Dabei ist es besonders wichtig, das Kind ganzheitlich zu betrachten.",
        "Die neusten wissenschaftlichen Erkenntnisse von Dr. Astrid Kopp-Duller zeigen, dass Kinder in den Bereichen: Aufmerksamkeit, Sinneswahrnehmung, sowie Lese und Schreibfähigkeit zu fördern sind. Wir setzen an den Ressourcen ihres Kindes an, damit es positive Erfahrungen sammeln kann und verbinden diese mit seinen Problembereichen.",
      ],
      isMainSection: true,
      icon: "book",
    },
    {
      title: "Was könnte eine Behandlung beinhalten?",
      content: [
        "Erstgespräch mit den Eltern",
        "Diagnostik und Erstellung eines individuellen Förderplans",
        "Einzeltraining und Übungsblätter für ein gezieltes Training zu Hause",
      ],
    },
    {
      title: "Elternworkshops",
      content: [
        "Wir bieten regelmäßige Elternworkshops zu verschiedenen Themen an: Impulskontrolle, Fit für die Schule, Stifthaltung, Hausaufgaben leicht gemacht, Familienregeln sowie Neurodivergenz: AD(H)S und Autismus-Spektrum. Die Workshops vermitteln praxisnahe Strategien für den Familienalltag und geben Einblicke in neurobiologische Zusammenhänge, Diagnostik und die Chancen einer frühzeitigen Unterstützung.",
      ],
      details: [
        {
          label: "Wer",
          value:
            "Eltern, deren Kinder sich bei uns in der Ergotherapie befinden, sowie für außenstehende Eltern (als Selbstzahlerleistung)",
        },
        {
          label: "Kosten",
          value: "Als Selbstzahlerleistung pro Elternworkshop 17 €",
        },
        {
          label: "Dauer",
          value: "1,5 Stunden",
        },
        {
          label: "Angeboten von",
          value: "Charlotte Fröhlich, Jana Deutschmann, Paula Nagel",
        },
      ],
      isMainSection: true,
      icon: "group",
    },
    {
      title: "Energiemanagement Schulung",
      content: [
        'Individuelles Erstgespräch, fünf Gruppenlektionen zu Themen wie Pausenmanagement und wirkungsvolle Kommunikation, individuelles Einzelgespräch zum Thema "meine Ziele im Alltag", begleitendes Selbsttraining für zu Hause',
      ],
      details: [
        {
          label: "Wer",
          value:
            "Klienten:Innen mit Fatigue, z.B. nach Post-Covid Erkrankung, Krebserkrankung, Schlaganfall, Schädel-Hirn-Trauma, Morbus Parkinson, Multiple Sklerose, Narkolepsie",
        },
        {
          label: "Kosten",
          value: "Mögliche Kostenübernahme durch die Krankenkasse",
        },
        {
          label: "Angeboten von",
          value: "Sandra Meyer",
        },
      ],
      isMainSection: true,
      icon: "academic",
    },
    {
      title: "Handtherapie",
      content: [
        "Rheumatische Erkrankungen (z.B. Arthritis)",
        "Verletzungen der Sehnen, Bänder, Gelenkkapseln und Knochen",
        "Karpaltunnelsyndrom, M. Duypuytren",
        "Morbus Sudeck",
        "Vor- und Nachbehandlung von Operationen",
        "Postoperativen Narben",
      ],
      texts: [
        "Eine Handtherapie kommt dann zum Tragen, wenn die Funktionen der Hand durch eine Schädigung in ihrer Funktionalität beeinträchtigt ist. Durch kleinste Verletzungen der Hand, kann es bereits zu gravierenden Einschränkungen im Alltag und oder Berufsleben kommen. Je nach Art und Verletzung werden unterschiedliche ergotherapeutische Maßnahmen ergriffen, um die Funktionalität der Hand wieder herzustellen und/oder zu verbessern.",
      ],
      isMainSection: true,
      icon: "hand",
    },
    {
      title: "Was beinhaltet eine motorisch- funktionelle Behandlung?",
      content: [
        "Narbenbehandlung (professionelle Nachsorge durch entsprechende Pflege und Massage der Narbe)",
        "Verbesserung der Feinmotorik und der manuellen Geschicklichkeit",
        "Schmerzreduktion",
        "Wiederaufnahme von Alltagstätigkeiten oder das Erlernen von Kompensationsstrategien",
        "Erlernen von Gelenkschutzmaßnahmen zur Reduzierung von schmerzbedingten Reaktionen",
        "Verbesserung der Sensibilität",
        "Verbesserung der Beweglichkeit, der Muskelkraft, der Koordination, der Ausdauer und der Belastbarkeit",
        "Thermische Anwendungen (Wärme/Kälte) z.B. Vorbereitung und Unterstützung von aktiver und/oder passiver Mobilisation",
        "Taping = funktionelle Verbandstechnik, Gelenke können dadurch stabilisiert werden und vor weiteren Traumatisierungen geschützt werden.",
      ],
    },
    {
      title: "Was noch berücksichtigt werden sollte?",
      content: [
        "Psychische Stabilisierung durch begleitende Gespräche (z.B. zur Krankheitsverarbeitung)",
        "Nach Möglichkeit: Übungen für ein Eigentraining zu Hause",
        "Eventuell Einbeziehung von Angehörigen",
        "ggf. Arbeitsplatzgestaltung zur Schmerzreduzierung",
      ],
    },
    {
      title: "Migränetherapie nach Kern",
      content: [],
      texts: [
        "Die Migränetherapie nach Kern ist eine manuelle Therapieform, die speziell zur Behandlung von Migräne und chronischen Kopfschmerzen entwickelt wurde. Wir bieten eine kostenfreie Migränesprechstunde an.",
      ],
      details: [
        {
          label: "Wer",
          value:
            "Menschen mit Migräne, Klienten mit Spannungskopfschmerzen, Personen mit Clusterkopfschmerz, Kopfschmerz nach Schleudertrauma",
        },
        { label: "Kosten", value: "Je nach Zeitaufwand zwischen 65-80€" },
        { label: "Angeboten von", value: "Lisa Spreitzer" },
      ],
      isMainSection: true,
      icon: "bolt",
    },
    {
      title: "Sexualität - raus aus dem Tabu !",
      content: [],
      texts: [
        "Sexualität, Nähe und Partnerschaft gehören zu den grundlegenden Bedürfnissen jedes Menschen. Dennoch werden Menschen mit körperlichen, geistigen oder psychischen Einschränkungen in diesem Bereich häufig übersehen oder ihre Bedürfnisse werden tabuisiert.",
        "Wir schaffen einen geschützten Raum für Gespräche über Wünsche, Unsicherheiten, Ängste und Fragen rund um Sexualität, Beziehung und Intimität. Wir informieren über Unterstützungsangebote, stärken die Selbstbestimmung und begleiten Betroffene, Angehörige sowie Einrichtungen bei einem offenen und respektvollen Umgang mit diesem wichtigen Lebensthema. Es werden auch Fachvorträge und Workshops angeboten.",
        "Denn jeder Mensch hat das Recht auf Nähe, Zugehörigkeit und gelebte Sexualität.",
      ],
      details: [
        { label: "Angeboten von", value: "Lisa Spreitzer" },
      ],
      isMainSection: true,
      icon: "heart",
    },
  ],
};

const testimonials = [
  {
    quote:
      "Mein Morbus Sudeck hat mein Leben ganz schön auf den Kopf gestellt. Dank der Ergotherapie kann ich meine Hand wieder im Alltag einsetzen. Auch die Gespräche über meine Ängste und Sorgen haben mir sehr gut getan.",
    section: "Handtherapie",
  },
];

const IconComponent: React.FC<{ icon?: OfferSection["icon"] }> = ({ icon }) => {
  switch (icon) {
    case "book":
      return <BookOpenIcon className="h-6 w-6" />;
    case "academic":
      return <AcademicCapIcon className="h-6 w-6" />;
    case "hand":
      return <HandRaisedIcon className="h-6 w-6" />;
    case "heart":
      return <HeartIcon className="h-6 w-6" />;
    case "group":
      return <UserGroupIcon className="h-6 w-6" />;
    case "bolt":
      return <BoltIcon className="h-6 w-6" />;
    default:
      return null;
  }
};

export default function OtherOffersPage() {
  return (
    <>
      <SEOHelmet page="otherOffers" pathname={PATH_ADDITIONAL_OFFERS} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalBusiness",
          "name": "Ergotherapie Rottweil - Lisa Spreitzer",
          "description": "Zusatzangebote: Energiemanagement-Schulungen, Legasthenietraining, Elternworkshops, Handtherapie, Migränetherapie und Sexualitätsberatung.",
          "url": "https://ergorw.de/zusatzangebote",
          "telephone": "+49-741-89698293",
          "email": "info@ergorw.de",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Auf dem Wall 29",
            "addressLocality": "Rottweil",
            "addressRegion": "Baden-Württemberg",
            "postalCode": "78628",
            "addressCountry": "DE"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "48.1684",
            "longitude": "8.6278"
          },
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "08:00",
              "closes": "18:00"
            },
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": "Saturday",
              "opens": "07:00",
              "closes": "17:00"
            }
          ],
          "founder": {
            "@type": "Person",
            "name": "Lisa Spreitzer",
            "jobTitle": "Ergotherapeutin"
          },
          "makesOffer": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Course",
                "name": "Energiemanagement Schulung",
                "description": "Schulung für Menschen mit Fatigue nach Post-Covid, Krebs, Schlaganfall oder anderen Erkrankungen"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "MedicalTherapy",
                "name": "Legasthenietraining nach der AFS-Methode",
                "description": "Ganzheitliches Training zur Verbesserung der Lese- und Schreibfähigkeit"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Course",
                "name": "Elternworkshops",
                "description": "Regelmäßige Workshops für Eltern zu Themen wie Impulskontrolle, Schule, Stifthaltung und Neurodivergenz"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "MedicalTherapy",
                "name": "Handtherapie",
                "description": "Spezialisierte Therapie bei Handverletzungen, Arthritis und anderen Handerkrankungen"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "MedicalTherapy",
                "name": "Migränetherapie nach Kern",
                "description": "Manuelle Therapieform zur Behandlung von Migräne und chronischen Kopfschmerzen mit kostenfreier Migränesprechstunde"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Course",
                "name": "Sexualität - raus aus dem Tabu !",
                "description": "Beratung, Fachvorträge und Workshops zu Sexualität, Nähe und Partnerschaft für Betroffene, Angehörige und Einrichtungen"
              }
            }
          ],
          "areaServed": {
            "@type": "City",
            "name": "Rottweil"
          },
          "sameAs": [
            "https://www.instagram.com/ergotherapie_zeittraeume/"
          ]
        })}
      </script>

      <main className="space-y-8 sm:space-y-14">
        {/* Main sections */}
        {data.sections.map((section, index) => (
          <article key={index}>
            {section.isMainSection ? (
              section.title === "Weitere Angebote" ? (
                // Intro section without card styling
                <section aria-labelledby={`section-${index}`} className="relative">
                  <div className="relative">
                    <h1
                      id={`section-${index}`}
                      className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900 dark:text-white mb-6"
                    >
                      {section.title}
                    </h1>
                    <div className="space-y-3">
                      {section.texts?.map((text, index) => (
                        <p
                          key={index}
                          className="text-base text-gray-500 dark:text-gray-400 leading-relaxed"
                        >
                          {text}
                        </p>
                      ))}
                    </div>
                  </div>
                </section>
              ) : (
                // Main sections with full content and card styling
                <section aria-labelledby={`section-${index}`} className="relative">
                  <div className="relative">
                    {/* Header with Icon */}
                    <div className="flex items-start sm:items-center gap-4 sm:gap-6 mb-4">
                      {section.icon && (
                        <div
                          className="p-3 sm:p-4 rounded-xl bg-ergo-100/80 dark:bg-ergo-900/80
                          shadow-sm
                          transform group-hover:scale-105 transition-all duration-300 flex-shrink-0"
                        >
                          <div className="text-ergo-600 dark:text-ergo-400">
                            <IconComponent icon={section.icon} />
                          </div>
                        </div>
                      )}
                      <h2
                        id={`section-${index}`}
                        className="text-xl sm:text-2xl font-semibold tracking-tight text-gray-900 dark:text-white"
                      >
                        {section.title}
                      </h2>
                    </div>

                    {/* Content Card */}
                    <div
                      className="relative bg-white dark:bg-gray-800/60 rounded-xl sm:rounded-2xl
                      shadow-sm
                      border border-gray-200/60 dark:border-gray-700/40 p-6 sm:p-8 md:p-10
                      transition-all duration-300"
                    >
                      <div className="space-y-6 sm:space-y-8">
                        {section.content.length > 0 && (
                          <div className="space-y-3">
                            {section.content.map((item, index) => (
                              <p
                                key={index}
                                className="text-base text-gray-500 dark:text-gray-400 leading-relaxed"
                              >
                                {item}
                              </p>
                            ))}
                          </div>
                        )}

                        {section.texts?.map((text, index) => (
                          <p
                            key={index}
                            className="text-base text-gray-500 dark:text-gray-400 leading-relaxed"
                          >
                            {text}
                          </p>
                        ))}

                        {section.details && (
                          <dl className="grid gap-4 sm:gap-6">
                            {section.details.map((detail, index) => (
                              <div key={index} className="flex flex-col sm:flex-row sm:gap-4">
                                <dt className="text-base sm:text-lg font-medium text-gray-900 dark:text-white sm:w-32 flex-shrink-0">
                                  {detail.label}
                                </dt>
                                <dd className="text-base text-gray-500 dark:text-gray-400 leading-relaxed">
                                  {detail.value}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        )}
                      </div>
                    </div>

                    {/* Testimonial for specific section */}
                    {testimonials.map(
                      (testimonial, tIndex) =>
                        testimonial.section === section.title && (
                          <div key={tIndex} className="mt-8">
                            <TestimonialsComponent testimonials={[{ quote: testimonial.quote }]} />
                          </div>
                        )
                    )}
                  </div>
                </section>
              )
            ) : (
              // Supporting sections with simpler styling
              <section aria-labelledby={`section-${index}`} className="relative">
                <div className="relative">
                  <h2
                    id={`section-${index}`}
                    className="text-xl sm:text-2xl font-semibold tracking-tight text-ergo-600 dark:text-ergo-400 mb-4"
                  >
                    {section.title}
                  </h2>
                  <div className="space-y-3">
                    {section.content.map((item, index) => (
                      <p key={index} className="text-base text-gray-500 dark:text-gray-400 leading-relaxed">
                        {item}
                      </p>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </article>
        ))}
      </main>
    </>
  );
}
